// src/components/SEO/PageMeta.js
// Reusable per-page SEO meta tags: title, description, canonical,
// hreflang alternates, Open Graph and Twitter cards.
//
// NOTE: this used to render through react-helmet-async, which is unmaintained
// and silently no-ops on React 19 - every tag below was being dropped. React 19
// hoists <title>, <meta> and <link> to <head> natively, so we render them
// directly and the library is no longer needed.
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { URL_PREFIX, PATH_LANG, INDEXABLE_LANGS, prefixFor } from '../../langPath';

const BASE_URL = 'https://robomindsystems.ai';
const OG_IMAGE = `${BASE_URL}/robomind-og-image.png`;
// Only languages that actually have their own indexable URLs may appear in
// hreflang. ro/de still render client-side only, so declaring alternates for
// them would point Google at URLs that serve English - a worse signal than
// declaring nothing. Add them here when their trees are prerendered.
const ALTERNATES = INDEXABLE_LANGS.map((lang) => ({ lang, prefix: prefixFor(lang) }));

// Search engines cut snippets at ~160-165 characters. Rather than let them chop a
// sentence mid-word, trim the meta description ourselves: prefer the last full
// sentence that fits, otherwise the last whole word plus an ellipsis. Open
// Graph keeps the full text - social cards show more.
const META_DESC_MAX = 165;
export function clampDescription(text, max = META_DESC_MAX) {
    const s = String(text || '').replace(/\s+/g, ' ').trim();
    if (s.length <= max) return s;
    const head = s.slice(0, max);
    const sentenceEnd = Math.max(head.lastIndexOf('. '), head.lastIndexOf('! '), head.lastIndexOf('? '));
    if (sentenceEnd >= 90) return head.slice(0, sentenceEnd + 1);
    const wordEnd = head.slice(0, max - 1).lastIndexOf(' ');
    return `${head.slice(0, wordEnd).replace(/[\s,;:–-]+$/, '')}…`;
}

const PageMeta = ({
    title, description, path = '/', keywords, noAlternates = false, alternates = null,
    ogType = 'website', image = null, article = null,
}) => {
    const { i18n } = useTranslation();
    const currentLang = (i18n.language || 'en').split('-')[0];
    // Canonical follows the URL TREE, not the displayed language: a Hungarian
    // visitor auto-detected on the English root is still on the English URL.
    const url = `${BASE_URL}${URL_PREFIX}${path === '/' ? '' : path}` || BASE_URL;
    const canonical = url === BASE_URL ? `${BASE_URL}/` : url;
    const alt = (prefix) => {
        const u = `${BASE_URL}${prefix}${path === '/' ? '' : path}`;
        return u === BASE_URL ? `${BASE_URL}/` : u;
    };
    // Long page names push the full brand past the ~60 character SERP cut-off;
    // fall back to the short brand so the keyword part stays intact.
    const BRAND = ' | ROBOMIND Systems';
    const pageTitle = title && title.length > 60 && title.endsWith(BRAND)
        ? `${title.slice(0, -BRAND.length)} | ROBOMIND`
        : title;
    const metaDescription = clampDescription(description);
    const ogImage = image ? (image.startsWith('http') ? image : `${BASE_URL}${image}`) : OG_IMAGE;
    const treeLang = PATH_LANG || currentLang;
    const ogLocale =
        treeLang === 'hu' ? 'hu_HU' :
        treeLang === 'ro' ? 'ro_RO' :
        treeLang === 'de' ? 'de_DE' : 'en_US';

    // React 19 does not support rendering <html lang>, so set it imperatively.
    useEffect(() => {
        if (currentLang) document.documentElement.setAttribute('lang', currentLang);
    }, [currentLang]);

    return (
        <>
            <title>{pageTitle}</title>
            <meta name="description" content={metaDescription} />
            {keywords && <meta name="keywords" content={keywords} />}
            <meta name="author" content="ROBOMIND Systems" />
            <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
            <link rel="canonical" href={canonical} />

            {/* Pages that exist in only one language must not advertise
                alternates that do not exist. */}
            {/* Pages whose slug differs per language pass an explicit cluster. */}
            {!noAlternates && (alternates
                ? alternates.map(({ lang, href }) => (
                    <link key={lang} rel="alternate" hrefLang={lang} href={href} />
                ))
                : ALTERNATES.map(({ lang, prefix }) => (
                    <link key={lang} rel="alternate" hrefLang={lang} href={alt(prefix)} />
                )))}
            {!noAlternates && (
                <link rel="alternate" hrefLang="x-default"
                    href={alternates ? alternates.find((a) => a.lang === 'en').href : alt('')} />
            )}

            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={canonical} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImage} />
            {!image && <meta property="og:image:width" content="1200" />}
            {!image && <meta property="og:image:height" content="630" />}
            <meta property="og:image:alt" content="ROBOMIND Systems - Industrial AI Solutions" />
            <meta property="og:locale" content={ogLocale} />
            <meta property="og:site_name" content="ROBOMIND Systems" />

            {article && article.published && <meta property="article:published_time" content={article.published} />}
            {article && article.modified && <meta property="article:modified_time" content={article.modified} />}
            {article && <meta property="article:author" content="ROBOMIND Systems" />}

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonical} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={metaDescription} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:image:alt" content="ROBOMIND Systems - Industrial AI Solutions" />
        </>
    );
};

export default PageMeta;
